import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { LexicalComposer }      from '@lexical/react/LexicalComposer';
import { RichTextPlugin }       from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable }      from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin }        from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin }           from '@lexical/react/LexicalListPlugin';
import { LinkPlugin }           from '@lexical/react/LexicalLinkPlugin';
import { OnChangePlugin }       from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, QuoteNode, $createHeadingNode, $createQuoteNode, $isHeadingNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import {
  INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import {
  $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND,
  UNDO_COMMAND, REDO_COMMAND,
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createParagraphNode } from 'lexical';
import { adminGet, adminPost } from '../api/apiClient';
import { BlogPostView } from '../components/blog/BlogPostView';
import { AdminImageCropper } from './AdminImageCropper';

// ─────────────────────────────────────────────────────────────────────────────
// Lexical theme
// ─────────────────────────────────────────────────────────────────────────────

const EDITOR_THEME = {
  heading: { h2: 'lx-h2', h3: 'lx-h3', h4: 'lx-h4' },
  quote:   'lx-quote',
  list:    { ul: 'lx-ul', ol: 'lx-ol', listitem: 'lx-li' },
  link:    'lx-link',
  text:    { bold: 'lx-bold', italic: 'lx-italic', underline: 'lx-underline', code: 'lx-code' },
};

const EDITOR_NODES = [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AutoLinkNode];

// ─────────────────────────────────────────────────────────────────────────────
// Toolbar
// ─────────────────────────────────────────────────────────────────────────────

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold]       = useState(false);
  const [isItalic, setIsItalic]   = useState(false);
  const [blockType, setBlockType] = useState('paragraph');

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        setIsBold(selection.hasFormat('bold'));
        setIsItalic(selection.hasFormat('italic'));
        const anchor  = selection.anchor.getNode();
        const element = anchor.getKey() === 'root'
          ? anchor
          : anchor.getTopLevelElementOrThrow();
        const type = $isHeadingNode(element) ? element.getTag() : element.getType();
        setBlockType(type || 'paragraph');
      });
    });
  }, [editor]);

  const formatBlock = (type) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      if (type === 'paragraph') {
        $setBlocksType(selection, () => $createParagraphNode());
      } else if (['h2', 'h3', 'h4'].includes(type)) {
        $setBlocksType(selection, () => $createHeadingNode(type));
      } else if (type === 'quote') {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  const toggleList = (listType) => {
    if (listType === 'ul') {
      if (blockType === 'ul') editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      else editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      if (blockType === 'ol') editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      else editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  };

  return (
    <div className="lexical-toolbar">
      <button
        className={`lexical-toolbar__btn${isBold ? ' lexical-toolbar__btn--active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold'); }}
        title="Pogrubienie"
        type="button"
      >
        <strong>B</strong>
      </button>
      <button
        className={`lexical-toolbar__btn${isItalic ? ' lexical-toolbar__btn--active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic'); }}
        title="Kursywa"
        type="button"
      >
        <em>I</em>
      </button>

      <div className="lexical-toolbar__sep" />

      <select
        className="lexical-toolbar__select"
        value={blockType}
        onChange={(e) => formatBlock(e.target.value)}
      >
        <option value="paragraph">Akapit</option>
        <option value="h2">Nagłówek H2</option>
        <option value="h3">Nagłówek H3</option>
        <option value="h4">Nagłówek H4</option>
        <option value="quote">Cytat</option>
      </select>

      <div className="lexical-toolbar__sep" />

      <button
        className={`lexical-toolbar__btn${blockType === 'ul' ? ' lexical-toolbar__btn--active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); toggleList('ul'); }}
        title="Lista wypunktowana"
        type="button"
      >
        &#8226;&#8212;
      </button>
      <button
        className={`lexical-toolbar__btn${blockType === 'ol' ? ' lexical-toolbar__btn--active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); toggleList('ol'); }}
        title="Lista numerowana"
        type="button"
      >
        1&#8212;
      </button>

      <div className="lexical-toolbar__sep" />

      <button
        className="lexical-toolbar__btn"
        onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(UNDO_COMMAND, undefined); }}
        title="Cofnij"
        type="button"
      >
        ↩
      </button>
      <button
        className="lexical-toolbar__btn"
        onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(REDO_COMMAND, undefined); }}
        title="Ponów"
        type="button"
      >
        ↪
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

function slugify(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

function Toast({ message, isError, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return <div className={`admin-toast${isError ? ' admin-toast--error' : ''}`}>{message}</div>;
}

export function AdminBlogEditor({ onToast }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  // Form state
  const [title, setTitle]                   = useState('');
  const [slug, setSlug]                     = useState('');
  const [excerpt, setExcerpt]               = useState('');
  const [contentJson, setContentJson]       = useState('');
  const [coverImageUrl, setCoverImageUrl]   = useState('');
  const [coverImageAlt, setCoverImageAlt]   = useState('');
  const [status, setStatus]                 = useState('draft');
  const [metaTitle, setMetaTitle]           = useState('');
  const [metaDesc, setMetaDesc]             = useState('');
  const [slugAutoSync, setSlugAutoSync]     = useState(true);

  // Preview state (debounced copy of form)
  const [previewPost, setPreviewPost]       = useState(null);

  // UI state
  const [loading, setLoading]               = useState(isEditing);
  const [saving, setSaving]                 = useState(false);
  const [toast, setToast]                   = useState(null);
  const [cropperSrc, setCropperSrc]         = useState(null);
  const [editorKey, setEditorKey]           = useState(0);
  const [initialContentJson, setInitialContentJson] = useState(null);

  // Load existing post if editing
  useEffect(() => {
    if (!isEditing) return;
    adminGet(`/admin/blog-get.php?id=${id}`)
      .then((post) => {
        setTitle(post.title || '');
        setSlug(post.slug || '');
        setExcerpt(post.excerpt || '');
        setContentJson(post.content_json || '');
        setInitialContentJson(post.content_json || '');
        setCoverImageUrl(post.cover_image_url || '');
        setCoverImageAlt(post.cover_image_alt || '');
        setStatus(post.status || 'draft');
        setMetaTitle(post.meta_title || '');
        setMetaDesc(post.meta_description || '');
        setSlugAutoSync(false);
        setEditorKey((k) => k + 1); // remount editor with loaded content
        setLoading(false);
      })
      .catch(() => {
        setToast({ message: 'Nie udało się załadować wpisu.', isError: true });
        setLoading(false);
      });
  }, [id, isEditing]);

  // Update preview (debounced 400ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setPreviewPost({
        title,
        slug,
        excerpt,
        content_json: contentJson,
        cover_image_url: coverImageUrl,
        cover_image_alt: coverImageAlt,
        author_name: 'Magdalena Pawlak',
        updated_at: new Date().toISOString(),
      });
    }, 400);
    return () => clearTimeout(t);
  }, [title, slug, excerpt, contentJson, coverImageUrl, coverImageAlt]);

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    if (slugAutoSync) setSlug(slugify(e.target.value));
  };

  const handleSlugChange = (e) => {
    setSlug(e.target.value);
    setSlugAutoSync(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropperSrc(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSave = async (overrideStatus) => {
    if (!title.trim()) { setToast({ message: 'Tytuł jest wymagany.', isError: true }); return; }
    const payload = {
      title, slug, excerpt, content_json: contentJson,
      cover_image_url: coverImageUrl, cover_image_alt: coverImageAlt,
      status: overrideStatus ?? status,
      meta_title: metaTitle, meta_description: metaDesc,
    };
    setSaving(true);
    try {
      if (isEditing) {
        await adminPost('/admin/blog-update.php', { id: Number(id), ...payload });
        setToast({ message: 'Wpis zaktualizowany.' });
      } else {
        const res = await adminPost('/admin/blog-create.php', payload);
        setToast({ message: 'Wpis zapisany.' });
        navigate(`/admin/blogs/${res.id}/edit`, { replace: true });
      }
    } catch (err) {
      setToast({ message: err.data?.error || 'Błąd zapisu.', isError: true });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p style={{ padding: 24, color: 'rgba(0,47,93,0.6)' }}>Ładowanie wpisu&hellip;</p>;
  }

  const editorInitialConfig = {
    namespace: 'BlogEditor',
    editorState: isEditing && initialContentJson ? initialContentJson : null,
    nodes: EDITOR_NODES,
    theme: EDITOR_THEME,
    onError: (err) => console.error('Lexical editor error:', err),
  };

  return (
    <div className="admin-editor">
      {toast && (
        <Toast
          message={toast.message}
          isError={toast.isError}
          onDone={() => setToast(null)}
        />
      )}

      {cropperSrc && (
        <AdminImageCropper
          imageSrc={cropperSrc}
          onClose={() => setCropperSrc(null)}
          onUploaded={(url) => { setCoverImageUrl(url); setCropperSrc(null); }}
        />
      )}

      {/* ── Header ── */}
      <div className="admin-editor__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/admin/blogs" style={{ color: 'rgba(0,47,93,0.5)', fontSize: 12, textDecoration: 'none' }}>
            ← Wpisy
          </Link>
          <h1 className="admin-editor__title">
            {isEditing ? 'Edytuj wpis' : 'Nowy wpis'}
          </h1>
        </div>
        <div className="admin-editor__header-actions">
          <button
            className="admin-btn admin-btn--secondary"
            onClick={() => handleSave('draft')}
            disabled={saving}
            type="button"
          >
            Zapisz szkic
          </button>
          <button
            className="admin-btn admin-btn--primary"
            onClick={() => handleSave('published')}
            disabled={saving}
            type="button"
          >
            {saving ? 'Zapisywanie…' : 'Opublikuj'}
          </button>
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div className="admin-editor__columns">
        {/* Left — editor */}
        <div className="admin-editor__left">

          {/* Basic info */}
          <div className="admin-editor__card">
            <p className="admin-editor__card-title">Podstawowe informacje</p>
            <div className="admin-editor__card-body">
              <div className="admin-editor__field">
                <label htmlFor="ae-title">Tytuł</label>
                <input id="ae-title" type="text" value={title} onChange={handleTitleChange} placeholder="Wpisz tytuł…" />
              </div>
              <div className="admin-editor__field">
                <label htmlFor="ae-slug">Slug (URL)</label>
                <input id="ae-slug" type="text" value={slug} onChange={handleSlugChange} placeholder="np. prawo-rodzinne-wroclaw" />
              </div>
              <div className="admin-editor__field">
                <label htmlFor="ae-excerpt">Krótki opis</label>
                <textarea id="ae-excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={3} placeholder="Opcjonalny skrót wpisu…" />
              </div>
              <div className="admin-editor__field">
                <label htmlFor="ae-status">Status</label>
                <select id="ae-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="draft">Szkic</option>
                  <option value="published">Opublikowany</option>
                </select>
              </div>
            </div>
          </div>

          {/* Content editor */}
          <div className="admin-editor__card">
            <p className="admin-editor__card-title">Treść</p>
            <div style={{ padding: 0 }}>
              <LexicalComposer key={editorKey} initialConfig={editorInitialConfig}>
                <div className="lexical-editor-wrap">
                  <ToolbarPlugin />
                  <div style={{ position: 'relative' }}>
                    <RichTextPlugin
                      contentEditable={<ContentEditable className="lexical-content-editable" />}
                      placeholder={<div className="lexical-placeholder">Zacznij pisać treść wpisu…</div>}
                      ErrorBoundary={LexicalErrorBoundary}
                    />
                  </div>
                </div>
                <HistoryPlugin />
                <ListPlugin />
                <LinkPlugin />
                <OnChangePlugin
                  onChange={(editorState) => {
                    const json = JSON.stringify(editorState.toJSON());
                    setContentJson(json);
                  }}
                />
              </LexicalComposer>
            </div>
          </div>

          {/* SEO */}
          <div className="admin-editor__card">
            <p className="admin-editor__card-title">SEO (opcjonalnie)</p>
            <div className="admin-editor__card-body">
              <div className="admin-editor__field">
                <label htmlFor="ae-meta-title">Meta tytuł</label>
                <input id="ae-meta-title" type="text" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} placeholder="Domyślnie: tytuł wpisu" maxLength={70} />
                <span className="admin-editor__char-count">{metaTitle.length}/70</span>
              </div>
              <div className="admin-editor__field">
                <label htmlFor="ae-meta-desc">Meta opis</label>
                <textarea id="ae-meta-desc" value={metaDesc} onChange={(e) => setMetaDesc(e.target.value)} rows={3} placeholder="Opis dla wyszukiwarek…" maxLength={320} />
                <span className="admin-editor__char-count">{metaDesc.length}/320</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right — cover + preview */}
        <div className="admin-editor__right">

          {/* Cover image */}
          <div className="admin-editor__card">
            <p className="admin-editor__card-title">Obraz główny</p>
            <div className="admin-editor__card-body">
              {coverImageUrl && (
                <img src={coverImageUrl} alt={coverImageAlt || 'Podgląd'} className="admin-editor__cover-preview" />
              )}
              <div className="admin-editor__upload-row">
                <label className="admin-editor__upload-btn" htmlFor="ae-cover-upload">
                  {coverImageUrl ? 'Zmień obraz' : 'Wybierz obraz'}
                </label>
                <input
                  id="ae-cover-upload"
                  type="file"
                  accept="image/*"
                  className="admin-editor__upload-input"
                  onChange={handleFileChange}
                />
                {coverImageUrl && (
                  <button
                    type="button"
                    className="admin-btn admin-btn--secondary"
                    style={{ fontSize: 11, padding: '5px 10px' }}
                    onClick={() => setCoverImageUrl('')}
                  >
                    Usuń
                  </button>
                )}
              </div>
              <div className="admin-editor__field" style={{ marginTop: 10 }}>
                <label htmlFor="ae-cover-alt">Tekst alternatywny (alt)</label>
                <input
                  id="ae-cover-alt"
                  type="text"
                  value={coverImageAlt}
                  onChange={(e) => setCoverImageAlt(e.target.value)}
                  placeholder="Krótki opis obrazu…"
                />
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className="admin-editor__card admin-editor__preview-panel">
            <p className="admin-editor__card-title">Podgląd</p>
            <div className="admin-editor__card-body">
              <BlogPostView post={previewPost} isPreview />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
